import { Link } from "@tanstack/react-router"
import { useServerState } from "@mylaunch/components/hooks"
import { LuMessageCircle, LuEye, LuHeart, LuShare2 } from "react-icons/lu"
import type { IconType } from "react-icons"
import { useAppMutation } from "~/hooks/useAppMutation"
import { likePost } from "~/features/feed/server/feed.functions"
import { ImSpinner2 } from "react-icons/im"
import type { Meta } from "@apps/gwservice/types"
import { useWebHaptics } from "web-haptics/react"

function formatCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`
  return String(n)
}

interface StatProps {
  icon: IconType | React.ReactNode
  value: number
  fill?: boolean
  href?: string
}

const StatContent = ({ icon: Icon, value, fill, onClick }: StatProps & { onClick?: () => void }) => {
  const { trigger } = useWebHaptics()
  const handleClick = onClick
    ? (event: React.MouseEvent) => {
        trigger("success")
        onClick()
        event.preventDefault()
      }
    : undefined

  return (
    <span className={`flex items-center gap-xs`} onClick={handleClick}>
      <div className="shrink-0 size-icon-s">
        {/* icon-s on div is needed for a loader; but it doesnt apply to icon for some reason */}
        {typeof Icon === "function" ? <Icon className="size-icon-s" fill={fill ? "var(--secondary)" : "none"} /> : Icon}
      </div>
      <span className="min-w-5">{formatCount(value)}</span>
    </span>
  )
}

function Stat({ icon: Icon, value, fill, href }: StatProps) {
  if (href) {
    return (
      <Link to={href}>
        <StatContent icon={Icon} value={value} fill={fill} />
      </Link>
    )
  }

  return <StatContent icon={Icon} value={value} fill={fill} />
}

interface LikeProps {
  postId: number
  likes: number
  likedByMe: boolean
}

const LikesCount = ({ likes: initialLikes, likedByMe, postId }: LikeProps) => {
  const [isLiked, setLiked] = useServerState(likedByMe)
  const [likesCount, setLikesCount] = useServerState(initialLikes)
  const errorMessage = `Не удалось ${isLiked ? "снять" : "поставить"} лайк`
  const { mutate, isPending } = useAppMutation<Meta | undefined, { liked: boolean }>({
    mutationFn: ({ liked }) => likePost({ data: { postId, liked } }),
    errorMessage,
    onSuccess: (meta) => onSuccess(meta),
  })

  if (isPending) return <StatContent icon={<ImSpinner2 className="animate-spin" />} value={likesCount} />
  return <StatContent icon={LuHeart} value={likesCount} fill={isLiked} onClick={() => mutate({ liked: !isLiked })} />

  function onSuccess(res: Meta | undefined) {
    if (!res) return
    setLiked((!!res.liked))
    setLikesCount((prev) => (res.liked ? prev + 1 : prev - 1))
  }
}

type EngagementRowProps = LikeProps & {
  views: number
  comments: number
}

export function EngagementRow({ postId, views, likes, comments, likedByMe }: EngagementRowProps) {
  const handleShare = async (event: React.MouseEvent) => {
    event.preventDefault()
    const postUrl = new URL(`/post/${postId}`, window.location.origin).toString()

    if (navigator.share) {
      await navigator.share({
        url: postUrl,
      })
    }
  }

  return (
    <div className="w-full flex items-center justify-between text-secondary-foreground px-m py-s">
      <div className="flex items-center gap-s">
        <Stat icon={LuEye} value={views} />
        <LikesCount likes={likes} likedByMe={likedByMe} postId={postId} />
        <Stat icon={LuMessageCircle} value={comments} />
      </div>
      <button onClick={handleShare} className="p-xs pr-0">
        <LuShare2 className="size-icon-s" />
      </button>
    </div>
  )
}
